import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import ChatSidebar from '../ChatSidebar';
import MobileAppNav from '../MobileAppNav';
import { getShopAuthState, logoutShopAccount } from '../../utils/shopAuthApi';
import {
    dismissShopNotification,
    fetchShopNotifications,
    getShopNotificationsStreamUrl,
    markAllShopNotificationsRead,
    markShopNotificationRead,
} from '../../utils/shopNotifications';
import { openResilientEventSource } from '../../utils/resilientEventSource';

const ANONYMOUS_AUTH_STATE = { status: 'anonymous', phone: '', email: '' };
// Waits out brief blips so the pill doesn't flash on every stream hiccup.
const OFFLINE_GRACE_MS = 2000;
const BACK_ONLINE_MS = 2500;

function ConnectionStatusPill({ isConnected }) {
    const [view, setView] = useState('hidden'); // hidden | offline | back

    useEffect(() => {
        if (!isConnected) {
            const timer = window.setTimeout(() => setView('offline'), OFFLINE_GRACE_MS);
            return () => window.clearTimeout(timer);
        }
        setView((current) => (current === 'offline' ? 'back' : current));
        return undefined;
    }, [isConnected]);

    useEffect(() => {
        if (view !== 'back') return undefined;
        const timer = window.setTimeout(() => setView('hidden'), BACK_ONLINE_MS);
        return () => window.clearTimeout(timer);
    }, [view]);

    if (view === 'hidden') return null;
    const offline = view === 'offline';
    return (
        <div
            role="status"
            aria-live="polite"
            className="pointer-events-none fixed left-1/2 top-16 z-[70] -translate-x-1/2 md:top-4"
        >
            <div className={`flex items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium shadow-md ${
                offline ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-200' : 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
            }`}>
                <span className={`h-2 w-2 rounded-full ${offline ? 'animate-pulse bg-amber-500' : 'bg-emerald-500'}`} />
                {offline ? 'No connection — reconnecting…' : 'Back online'}
            </div>
        </div>
    );
}

export default function PartnerShell() {
    const location = useLocation();
    const [status, setStatus] = useState('loading');
    const [shop, setShop] = useState(null);
    // Shared with Settings (via the outlet context) so a new photo shows in the sidebar at once.
    const [shopPhotoUrl, setShopPhotoUrl] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notificationsUnread, setNotificationsUnread] = useState(0);
    const [isBrowserOnline, setIsBrowserOnline] = useState(() => navigator.onLine !== false);
    // The notifications stream doubles as the server-reachability check: Wi-Fi can be up
    // while the server still can't be reached.
    const [isStreamConnected, setIsStreamConnected] = useState(true);

    useEffect(() => {
        const goOnline = () => setIsBrowserOnline(true);
        const goOffline = () => setIsBrowserOnline(false);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        getShopAuthState()
            .then((data) => {
                if (cancelled) return;
                if (data?.authenticated) {
                    setShop(data.shop || null);
                    setShopPhotoUrl(data.shop?.photo_url || '');
                    setStatus('authenticated');
                } else {
                    setStatus('anonymous');
                }
            })
            .catch(() => {
                if (!cancelled) setStatus('anonymous');
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const toggleSidebar = useCallback(() => setIsSidebarOpen((open) => !open), []);
    const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);
    const toggleCollapse = useCallback(() => setIsSidebarCollapsed((collapsed) => !collapsed), []);

    const refreshNotifications = useCallback(async () => {
        try {
            const { notifications: rows, unreadCount } = await fetchShopNotifications();
            setNotifications(rows);
            setNotificationsUnread(unreadCount);
        } catch (error) {
            if (error?.status === 401) return;
            console.error('Failed to load shop notifications:', error);
        }
    }, []);

    const markNotificationRead = useCallback(async (notificationId) => {
        setNotifications((prev) => {
            const target = prev.find((notification) => String(notification.id) === String(notificationId));
            if (target && !target.isRead) setNotificationsUnread((count) => Math.max(0, count - 1));
            return prev.map((notification) => (
                String(notification.id) === String(notificationId)
                    ? { ...notification, isRead: true, is_read: true }
                    : notification
            ));
        });
        try {
            await markShopNotificationRead(notificationId);
            await refreshNotifications();
        } catch (error) {
            console.error('Failed to mark shop notification read:', error);
            await refreshNotifications();
        }
    }, [refreshNotifications]);

    const markAllNotificationsRead = useCallback(async () => {
        setNotifications((prev) => prev.map((notification) => ({ ...notification, isRead: true, is_read: true })));
        setNotificationsUnread(0);
        try {
            await markAllShopNotificationsRead();
            await refreshNotifications();
        } catch (error) {
            console.error('Failed to mark shop notifications read:', error);
            await refreshNotifications();
        }
    }, [refreshNotifications]);

    const dismissNotification = useCallback(async (notificationId) => {
        setNotifications((prev) => {
            const target = prev.find((notification) => String(notification.id) === String(notificationId));
            if (target && !target.isRead) setNotificationsUnread((count) => Math.max(0, count - 1));
            return prev.filter((notification) => String(notification.id) !== String(notificationId));
        });
        try {
            await dismissShopNotification(notificationId);
            await refreshNotifications();
        } catch (error) {
            console.error('Failed to dismiss shop notification:', error);
            await refreshNotifications();
        }
    }, [refreshNotifications]);

    useEffect(() => {
        if (status !== 'authenticated') return undefined;
        let debounceTimer = null;
        const debouncedRefresh = () => {
            window.clearTimeout(debounceTimer);
            debounceTimer = window.setTimeout(refreshNotifications, 250);
        };

        refreshNotifications();
        const closeStream = openResilientEventSource(getShopNotificationsStreamUrl(), {
            listeners: { ready: debouncedRefresh, notification: debouncedRefresh },
            onOpen: () => {
                setIsStreamConnected(true);
                debouncedRefresh();
            },
            onError: () => setIsStreamConnected(false),
        });

        return () => {
            closeStream();
            window.clearTimeout(debounceTimer);
        };
    }, [refreshNotifications, status]);

    const handleLogout = useCallback(async () => {
        try {
            await logoutShopAccount();
        } catch (error) {
            console.error('Failed to sign out of the shop portal:', error);
        }
        window.location.assign('/login');
    }, []);

    // Must not redirect until /shop-auth/me has answered, or every refresh
    // bounces a signed-in shop back to the login page.
    if (status === 'loading') {
        return (
            <div className="flex h-[100dvh] items-center justify-center bg-[#F9F9F9]">
                <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#d9d9d9] border-t-[#D81F26]" />
            </div>
        );
    }

    if (status !== 'authenticated') {
        const next = `${location.pathname}${location.search}${location.hash}`;
        return <Navigate to={`/login?${new URLSearchParams({ next })}`} replace />;
    }

    return (
        <div className="relative flex h-[100dvh] flex-row overflow-hidden bg-gradient-to-br from-[#F9F9F9] to-[#F2F2F2]">
            <MobileAppNav authState={ANONYMOUS_AUTH_STATE} onMenuClick={toggleSidebar} isAdminView />
            <ConnectionStatusPill isConnected={isBrowserOnline && isStreamConnected} />

            {isSidebarOpen && (
                <div
                    className="fixed inset-0 z-[55] bg-black/40 backdrop-blur-sm transition-opacity duration-300 md:hidden"
                    onClick={closeSidebar}
                />
            )}

            <ChatSidebar
                isAdminView
                authState={ANONYMOUS_AUTH_STATE}
                adminAuthState={{ status: 'authenticated', authenticated: true }}
                displayName={shop?.name || ''}
                shopPhotoUrl={shopPhotoUrl}
                onLogout={handleLogout}
                isOpen={isSidebarOpen}
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={toggleCollapse}
                onClose={closeSidebar}
                notificationsUnread={notificationsUnread}
            />

            <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden pt-14 pb-[env(safe-area-inset-bottom)] transition-all duration-300 md:pt-0 md:pb-0">
                <div className="relative min-h-0 flex-1 overflow-hidden">
                    <Outlet context={{
                        notifications,
                        notificationsUnread,
                        markNotificationRead,
                        markAllNotificationsRead,
                        dismissNotification,
                        shopPhotoUrl,
                        setShopPhotoUrl,
                    }} />
                </div>
            </main>
        </div>
    );
}
